/* ==========================================================================
   Rook UI Core — Base Component
   Shared base for all rk-* custom elements.
   Provides: tiered hydration, refs, event delegation, event bus helpers.
   ========================================================================== */

const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback
    : (cb) => setTimeout(cb, 0);

const HYDRATION_TIERS = new Set(['eager', 'idle', 'visible', 'interaction']);

export class RkComponent extends HTMLElement {
  static hydration = 'eager';

  refs = {};

  #hydrated = false;
  #delegated = new Map(); // eventType -> handler
  #mutationObserver = null;
  #intersectionObserver = null;
  #interactionCleanup = null;

  connectedCallback() {
    if (this.#hydrated) return;

    const tier = this.#resolveTier();

    switch (tier) {
      case 'eager':
        this.#hydrate();
        break;

      case 'idle':
        requestIdleCallback(() => {
          if (this.isConnected) this.#hydrate();
        });
        break;

      case 'visible':
        this.#intersectionObserver = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.#intersectionObserver.disconnect();
            this.#intersectionObserver = null;
            this.#hydrate();
          }
        }, { rootMargin: '50px', threshold: 0 });
        this.#intersectionObserver.observe(this);
        break;

      case 'interaction':
        if (typeof this.attachHydrationTrigger === 'function') {
          this.#interactionCleanup = this.attachHydrationTrigger(
            () => this.#hydrate()
          );
        } else {
          // No trigger declared — fall back to eager.
          this.#hydrate();
        }
        break;
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
    this.#interactionCleanup?.();
    this.#interactionCleanup = null;

    for (const [eventType, handler] of this.#delegated) {
      this.removeEventListener(eventType, handler, true);
    }
    this.#delegated.clear();

    if (this.#hydrated) {
      try { this.teardown(); } catch (e) { console.error(e); }
      this.#hydrated = false;
    }
  }

  #resolveTier() {
    const override = this.getAttribute('data-hydrate');
    if (override && HYDRATION_TIERS.has(override)) return override;
    return this.constructor.hydration || 'eager';
  }

  #hydrate() {
    if (this.#hydrated) return;
    this.#hydrated = true;
    this.#updateRefs();

    // Start the MutationObserver synchronously BEFORE setup() runs so we don't
    // miss DOM mutations triggered by setup() itself (TOCTOU window).
    this.#mutationObserver = new MutationObserver((muts) => {
      if (muts.some((m) =>
        (m.type === 'attributes' && m.target instanceof Element) ||
        m.type === 'childList'
      )) {
        this.#updateRefs();
      }
    });
    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['ref'],
    });

    try {
      this.setup();
    } catch (e) {
      // Roll back to a clean unhydrated state so a future reconnect can retry
      // and teardown() won't fire on partial state.
      this.#hydrated = false;
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
      console.error(e);
    }
  }

  #updateRefs() {
    const refs = {};
    for (const el of this.querySelectorAll('[ref]')) {
      const name = el.getAttribute('ref') || '';
      const isArray = name.endsWith('[]');
      const key = isArray ? name.slice(0, -2) : name;
      if (isArray) {
        (refs[key] ||= []).push(el);
      } else {
        refs[key] = el;
      }
    }
    this.refs = refs;
  }

  /**
   * Bind delegated listeners. Map of "eventType selector" -> handler.
   * Selector is optional; without it, the listener fires for any event on `this`.
   * Listeners are registered in the CAPTURE phase so they fire even if a descendant
   * calls stopPropagation() during bubbling.
   */
  delegate(map) {
    const byEvent = new Map();

    for (const key of Object.keys(map)) {
      const space = key.indexOf(' ');
      const eventType = space === -1 ? key : key.slice(0, space);
      const selector = space === -1 ? null : key.slice(space + 1).trim();
      const handler = map[key].bind(this);

      if (!byEvent.has(eventType)) byEvent.set(eventType, []);
      byEvent.get(eventType).push({ selector, handler });
    }

    for (const [eventType, entries] of byEvent) {
      const dispatcher = (event) => {
        for (const { selector, handler } of entries) {
          if (!selector) {
            handler(event);
            continue;
          }
          const target = event.target instanceof Element
            ? event.target.closest(selector)
            : null;
          if (target && this.contains(target)) {
            handler(event, target);
          }
        }
      };

      const existing = this.#delegated.get(eventType);
      if (existing) this.removeEventListener(eventType, existing, true);

      this.addEventListener(eventType, dispatcher, true);
      this.#delegated.set(eventType, dispatcher);
    }
  }

  /**
   * Dispatch a custom event with `rk:` namespace, bubbling.
   */
  rkEmit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(`rk:${name}`, {
      bubbles: true,
      composed: false,
      detail,
    }));
  }

  /** Called once after hydration. Override in subclasses to bind listeners and init state. */
  setup() {}

  /** Called on disconnectedCallback after listeners are removed. Override to clean up observers, etc. */
  teardown() {}

  /**
   * Reserved hook for Section Rendering API integration (not yet wired up by base class).
   * Subclasses can override; the base class does not currently invoke this automatically.
   */
  updatedCallback() {}
}

/**
 * Document-level listener for `rk:*` events. Helper for cross-component bus.
 */
export function rkOn(name, handler) {
  document.addEventListener(`rk:${name}`, handler);
  return () => document.removeEventListener(`rk:${name}`, handler);
}
